// WebsiteSettings.jsx - Updated Design
import React, { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { updateOfferStatus } from "../store/offersSlice";
import axiosInstance from "../axiosInstance";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, Settings, Lock, Clock, Tag, AlertCircle, Eye, EyeOff, Trash2 } from "lucide-react";

function WebsiteSettings({ }) {
  const navigate = useNavigate();
  const goBack = () => navigate("/admin");
  const [shopOpen, setShopOpen] = useState(null);
  const [openTime, setOpenTime] = useState("");
  const [closeTime, setCloseTime] = useState("");
  const [manualOverride, setManualOverride] = useState(false);
  const [showOffersModal, setShowOffersModal] = useState(false);
  const [showCancelledModal, setShowCancelledModal] = useState(false);
  const [cancelledOrders, setCancelledOrders] = useState([]);
  const [showOfflineModal, setShowOfflineModal] = useState(false);
  const [offlineItems, setOfflineItems] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const offers = useSelector((state) => state.offers.list);
  const dispatch = useDispatch();

  useEffect(() => {
    const fetchShopStatus = async () => {
      try {
        const res = await axiosInstance.get("/admin/shop-status", {
          withCredentials: true,
        });
        setShopOpen(res.data.shop_open);
        setOpenTime(res.data.shop_open_time || "");
        setCloseTime(res.data.shop_close_time || "");
        setManualOverride(res.data.manual_override || false);
      } catch (err) {
        console.error("Error fetching shop status:", err);
      }
    };
    fetchShopStatus();
  }, []);

  const updateTimings = async () => {
    try {
      await axiosInstance.put(
        "/admin/update-shop-timings",
        {
          shop_open_time: openTime,
          shop_close_time: closeTime,
        },
        { withCredentials: true }
      );
      setManualOverride(false);
      alert("Shop timings updated successfully! Automatic scheduling resumed.");
    } catch (err) {
      console.error("Error updating shop timings:", err);
      alert("Failed to update shop timings.");
    }
  };

  const toggleShop = async () => {
    try {
      const updatedStatus = !shopOpen;
      await axiosInstance.put(
        "/admin/shop-toggle",
        { shop_open: updatedStatus },
        { withCredentials: true }
      );
      setShopOpen(updatedStatus);
      setManualOverride(true);
      alert(`Shop is now ${updatedStatus ? "open" : "closed"} (Manual Override Active)`);
    } catch (error) {
      console.error("Error toggling shop status:", error);
      alert("Failed to toggle shop status.");
    }
  };

  const handleCheckboxChange = async (offer) => {
    try {
      await axiosInstance.put(
        "/admin/offers/update",
        {
          offer_text: offer.offer_text,
          value: !offer.value,
        },
        { withCredentials: true }
      );
      dispatch(
        updateOfferStatus({ offer_text: offer.offer_text, value: !offer.value })
      );
    } catch (err) {
      console.error("Error updating offer:", err);
      alert("Failed to update offer status.");
    }
  };

  const fetchCancelledOrders = async () => {
    try {
      const res = await axiosInstance.get("/admin/orders/cancelled", {
        withCredentials: true,
      });
      setCancelledOrders(res.data.orders || []);
    } catch (err) {
      console.error("Error fetching cancelled orders:", err);
      alert("Failed to fetch cancelled orders.");
    }
  };

  const refundOrder = async (orderId) => {
    try {
      await axiosInstance.post(
        "/orders/update-status",
        {
          order_id: orderId,
          status: "refunded",
          driver_id: null,
        },
        { withCredentials: true }
      );
      alert(`Order #${orderId} refunded successfully!`);
      fetchCancelledOrders();
    } catch (err) {
      console.error("Error refunding order:", err);
      alert("Failed to refund order.");
    }
  };

  const fetchOfflineItems = async () => {
    try {
      const res = await axiosInstance.get("/item/unavailable-items", {
        withCredentials: true,
      });
      setOfflineItems(res.data || []);
    } catch (err) {
      console.error("Error fetching offline items:", err);
      alert("Failed to fetch offline items.");
    }
  };

  const makeItemAvailable = async (itemId) => {
    try {
      await axiosInstance.put(
        "/item/set-availability",
        { item_id: itemId, availability: true },
        { withCredentials: true }
      );
      alert(`Item #${itemId} marked available!`);
      fetchOfflineItems();
    } catch (err) {
      console.error("Error updating item:", err);
      alert("Failed to update item availability.");
    }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      {/* Header */}
      <div className="mb-8 md:mb-12">
        <button
          onClick={() => navigate("/admin")}
          className="group flex items-center gap-2 text-gray-600 hover:text-blue-600 transition-all mb-6 font-medium text-sm md:text-base"
          style={{ fontFamily: "Bambino" }}
        >
          <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
          <span>Back to Dashboard</span>
        </button>

        <div className="flex items-center justify-between gap-2">
          
          <h1
            className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold bg-gradient-to-r from-green-600 to-emerald-500 bg-clip-text text-transparent"
            style={{ fontFamily: "Bambino", lineHeight: "1.2" }}
          >
            Website Settings
          </h1>
          <Settings className="w-8 h-8 md:w-12 md:h-12 text-green-500" />
        </div>
      </div>

      {/* Shop Status Card */}
      <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl border-2 border-green-100 mb-6 md:mb-8">
        <div className="flex justify-between items-start md:items-center gap-4 flex-col md:flex-row">
          <div>
            <h2
              className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2 mb-4"
              style={{ fontFamily: "Bambino" }}
            >
              <Lock className="w-6 h-6 text-green-600" />
              Shop Status
            </h2>
            {manualOverride && (
              <div className="flex items-center gap-2 mt-3">
                <div className="w-2 h-2 bg-yellow-500 rounded-full animate-pulse"></div>
                <span
                  className="text-xs bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full font-semibold"
                  style={{ fontFamily: "Bambino" }}
                >
                  Manual Override Active
                </span>
              </div>
            )}
          </div>

          <label className="relative inline-flex items-center cursor-pointer">
            <input
              type="checkbox"
              className="sr-only peer"
              checked={shopOpen}
              onChange={toggleShop}
            />
            <div className="w-16 h-8 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-green-400 rounded-full peer peer-checked:bg-gradient-to-r peer-checked:from-green-400 peer-checked:to-emerald-400 transition duration-300"></div>
            <span className="absolute left-1 top-1 w-6 h-6 bg-white rounded-full transition-all duration-300 peer-checked:translate-x-8 shadow-md"></span>
          </label>
        </div>

        {manualOverride && (
          <div className="mt-4 p-4 bg-yellow-50 border-l-4 border-yellow-400 rounded-lg">
            <p className="text-sm text-yellow-800 flex items-start gap-2" style={{ fontFamily: "Bambino" }}>
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <span>Shop is in manual mode. Update timings below to resume automatic scheduling.</span>
            </p>
          </div>
        )}
      </div>

      {/* Shop Timings Card */}
      <div className="bg-white/70 backdrop-blur-md rounded-2xl md:rounded-3xl p-6 md:p-8 shadow-xl border-2 border-blue-100 mb-6 md:mb-8">
        <h2
          className="text-xl md:text-2xl font-bold mb-6 text-gray-800 flex items-center gap-2"
          style={{ fontFamily: "Bambino" }}
        >
          <Clock className="w-6 h-6 text-blue-600" />
          Shop Timings
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <div>
            <label
              className="block mb-3 font-bold text-gray-700 text-sm md:text-base"
              style={{ fontFamily: "Bambino" }}
            >
              Open Time
            </label>
            <input
              type="time"
              value={openTime}
              onChange={(e) => setOpenTime(e.target.value)}
              className="w-full border-2 border-blue-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all text-base"
              style={{ fontFamily: "Bambino" }}
            />
          </div>
          <div>
            <label
              className="block mb-3 font-bold text-gray-700 text-sm md:text-base"
              style={{ fontFamily: "Bambino" }}
            >
              Close Time
            </label>
            <input
              type="time"
              value={closeTime}
              onChange={(e) => setCloseTime(e.target.value)}
              className="w-full border-2 border-blue-200 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-300 focus:border-transparent transition-all text-base"
              style={{ fontFamily: "Bambino" }}
            />
          </div>
        </div>

        <button
          onClick={updateTimings}
          className="w-full bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-6 py-3 rounded-xl hover:from-blue-600 hover:to-cyan-600 transition-all shadow-lg hover:shadow-xl transform hover:scale-105 font-bold text-base md:text-lg"
          style={{ fontFamily: "Bambino" }}
        >
          {manualOverride ? "Update Timings & Resume Auto Schedule" : "Update Timings"}
        </button>
      </div>

      {/* Action Buttons */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
        <button
          onClick={() => {
            setShowOffersModal(true);
          }}
          className="group bg-white/70 backdrop-blur-md rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all border-2 border-purple-100 hover:border-purple-300 hover:scale-105"
          style={{ fontFamily: "Bambino" }}
        >
          <div className="flex items-center justify-center gap-3">
            <Tag className="w-6 h-6 text-purple-600 group-hover:scale-110 transition-transform" />
            <div className="text-left">
              <h3 className="font-bold text-gray-800">Manage Offers</h3>
              <p className="text-xs text-gray-600">Enable/disable promotions</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => {
            fetchCancelledOrders();
            setShowCancelledModal(true);
          }}
          className="group bg-white/70 backdrop-blur-md rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all border-2 border-orange-100 hover:border-orange-300 hover:scale-105"
          style={{ fontFamily: "Bambino" }}
        >
          <div className="flex items-center justify-center gap-3">
            <AlertCircle className="w-6 h-6 text-orange-600 group-hover:scale-110 transition-transform" />
            <div className="text-left">
              <h3 className="font-bold text-gray-800">Cancelled Orders</h3>
              <p className="text-xs text-gray-600">View & refund orders</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => {
            fetchOfflineItems();
            setShowOfflineModal(true);
          }}
          className="group bg-white/70 backdrop-blur-md rounded-xl md:rounded-2xl p-4 md:p-6 shadow-lg hover:shadow-xl transition-all border-2 border-red-100 hover:border-red-300 hover:scale-105"
          style={{ fontFamily: "Bambino" }}
        >
          <div className="flex items-center justify-center gap-3">
            <Trash2 className="w-6 h-6 text-red-600 group-hover:scale-110 transition-transform" />
            <div className="text-left">
              <h3 className="font-bold text-gray-800">Offline Items</h3>
              <p className="text-xs text-gray-600">Manage availability</p>
            </div>
          </div>
        </button>
      </div>

      {/* Offers Modal */}
      {showOffersModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 w-full max-w-md max-h-[80vh] overflow-y-auto shadow-2xl border-2 border-purple-100">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2" style={{ fontFamily: "Bambino" }}>
                <Tag className="w-6 h-6 text-purple-600" />
                Manage Offers
              </h2>
              <button
                onClick={() => setShowOffersModal(false)}
                className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="space-y-3" style={{ fontFamily: "Bambino" }}>
              {offers.map((offer, index) => (
                <label key={index} className="flex items-center gap-3 p-3 hover:bg-purple-50 rounded-lg transition-colors cursor-pointer">
                  <input
                    type="checkbox"
                    checked={offer.value}
                    onChange={() => handleCheckboxChange(offer)}
                    disabled={offer.locked}
                    className="w-5 h-5 cursor-pointer"
                  />
                  <span className="text-gray-700">{offer.offer_text}</span>
                </label>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Cancelled Orders Modal */}
      {showCancelledModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl md:rounded-3xl w-full max-w-2xl max-h-[90vh] shadow-2xl flex flex-col border-2 border-orange-100 overflow-hidden">
            <div className="bg-gradient-to-r from-orange-100 to-red-100 px-6 md:px-8 py-4 md:py-6 flex justify-between items-center sticky top-0 z-10">
              <h2 className="text-lg md:text-2xl font-bold text-gray-800" style={{ fontFamily: "Bambino" }}>
                Cancelled Orders
              </h2>
              <button
                onClick={() => setShowCancelledModal(false)}
                className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto p-6 md:p-8 space-y-6" style={{ fontFamily: "Bambino" }}>
              {(() => {
                const cardOrders = cancelledOrders.filter(o => o.status?.toLowerCase() === "cancelled" && o.payment_type?.toLowerCase() === "card");
                const cashOrders = cancelledOrders.filter(o => o.status?.toLowerCase() === "cancelled" && o.payment_type?.toLowerCase().includes("cash"));
                const refundedOrders = cancelledOrders.filter(o => o.status?.toLowerCase() === "refunded");

                const renderSection = (title, orders) => (
                  <div key={title}>
                    <h3 className="text-lg font-bold mb-4 pb-2 border-b-2 border-gray-200 text-gray-800">{title}</h3>
                    {orders.length === 0 ? (
                      <p className="text-gray-500">No {title.toLowerCase()} found.</p>
                    ) : (
                      orders.map((order) => (
                        <div key={order.order_id} className="border-2 border-gray-200 p-4 md:p-6 rounded-xl mb-4 hover:border-orange-300 transition-colors">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                            <div>
                              <p className="text-sm text-gray-600 font-semibold">Order ID</p>
                              <p className="text-lg font-bold text-gray-800">#{order.order_id}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600 font-semibold">Total</p>
                              <p className="text-lg font-bold text-green-600">£{order.total_price}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600 font-semibold">Customer</p>
                              <p className="text-gray-800">{order.customer_name}</p>
                            </div>
                            <div>
                              <p className="text-sm text-gray-600 font-semibold">Email</p>
                              <p className="text-gray-800 truncate">{order.customer_email}</p>
                            </div>
                          </div>
                          {order.status?.toLowerCase() !== "refunded" && title === "Card Orders" && (
                            <button
                              onClick={() => refundOrder(order.order_id)}
                              className="w-full bg-gradient-to-r from-red-500 to-red-600 text-white px-4 py-2 rounded-lg hover:from-red-600 hover:to-red-700 transition-all font-semibold mt-4"
                            >
                              Process Refund
                            </button>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                );

                return (
                  <>
                    {renderSection("Card Orders", cardOrders)}
                    {renderSection("Cash Orders", cashOrders)}
                    {renderSection("Refunded Orders", refundedOrders)}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      )}

      {/* Offline Items Modal */}
      {showOfflineModal && (
        <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white rounded-2xl md:rounded-3xl p-6 md:p-8 w-full max-w-2xl max-h-[80vh] overflow-y-auto shadow-2xl border-2 border-red-100">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-xl md:text-2xl font-bold text-gray-800 flex items-center gap-2" style={{ fontFamily: "Bambino" }}>
                <Trash2 className="w-6 h-6 text-red-600" />
                Offline Items
              </h2>
              <button
                onClick={() => setShowOfflineModal(false)}
                className="text-gray-400 hover:text-gray-600 text-3xl leading-none"
              >
                ×
              </button>
            </div>
            <div className="space-y-3" style={{ fontFamily: "Bambino" }}>
              {offlineItems.length === 0 ? (
                <p className="text-gray-500 text-center py-8">No offline items found.</p>
              ) : (
                offlineItems.map((item) => (
                  <div
                    key={item.item_id}
                    className="border-2 border-red-100 p-4 rounded-xl flex flex-col md:flex-row md:justify-between md:items-center hover:border-red-300 transition-colors gap-4"
                  >
                    <div>
                      <p className="font-bold text-gray-800">{item.item_name}</p>
                      <p className="text-sm text-gray-600">Type: {item.type}</p>
                    </div>
                    <button
                      onClick={() => makeItemAvailable(item.item_id)}
                      className="bg-gradient-to-r from-green-500 to-emerald-500 text-white px-6 py-2 rounded-lg hover:from-green-600 hover:to-emerald-600 transition-all font-semibold whitespace-nowrap"
                    >
                      Make Available
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default WebsiteSettings;